import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "checkbox",
    "filterInput",
    "cards",
    "filterForm",
    "categoryForm",
    "searchForm",
    "searchInput",
    "count",
    "pagination",
    // newly used targets in updateFromHtml
    "headingTitle",
    "categoryList",
    "categoryCheckbox",
  ]

  connect() {
    // console.log(this.filterFormTarget)
    // this.checkboxTargets.forEach(checkbox => {
    //   checkbox.addEventListener("change", this.updateFilters.bind(this))
    // })

    // Delegate pagination clicks to fetch without reload
    if (this.hasPaginationTarget) {
      this._onPaginationClick = (e) => {
        const link = e.target.closest('a')
        if (!link) return
        const href = link.getAttribute('href')
        if (!href) return
        // Only intercept same-origin links
        const url = new URL(href, window.location.origin)
        if (url.origin !== window.location.origin) return
        e.preventDefault()
        history.pushState({}, "", url)
        this.fetchAndUpdate(url)
      }
      this.paginationTarget.addEventListener('click', this._onPaginationClick)
    }

    // Keep support for a 'Clear all' button using data-category=""
    if (this.hasCategoryListTarget) {
      this._onCategoryClick = (e) => {
        const el = e.target.closest('[data-category]')
        if (!el) return
        e.preventDefault()
        const url = new URL(window.location.href)
        const slug = el.dataset.category || ""
        if (slug) {
          url.searchParams.set('category', slug)
        } else {
          url.searchParams.delete('category')
        }
        history.pushState({}, "", url)
        this.fetchAndUpdate(url)
      }
      this.categoryListTarget.addEventListener('click', this._onCategoryClick)
    }

    // Handle back/forward navigation to re-fetch content
    this._onPopState = () => {
      const url = new URL(window.location.href)
      this.fetchAndUpdate(url)
    }
    window.addEventListener('popstate', this._onPopState)
  }

  disconnect() {
    if (this._onPaginationClick && this.hasPaginationTarget) {
      this.paginationTarget.removeEventListener('click', this._onPaginationClick)
    }
    if (this._onCategoryClick && this.hasCategoryListTarget) {
      this.categoryListTarget.removeEventListener('click', this._onCategoryClick)
    }
    if (this._onPopState) {
      window.removeEventListener('popstate', this._onPopState)
    }
    clearTimeout(this._searchTimer)
  }

  handleFilterForm() {
    const currentURL = new URL(window.location.href)
    const type_filter = Array.from(this.filterFormTarget.querySelectorAll("input[type=checkbox]"))
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.name)
      .join(",")
    type_filter.length ? currentURL.searchParams.set("type_filter", type_filter) : currentURL.searchParams.delete("type_filter")
    history.pushState({}, "", currentURL)
    this.fetchAndUpdate(currentURL)
  }

  updateFilters() {
    this.filterInputTarget.value = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
      .join(",")

    this.submitForm()
  }

  async submitForm() {
    const url = new URL(window.location.href)
    url.searchParams.set("type_filter", this.filterInputTarget.value)

    this.toggleLoader(true)

    try {
      const response = await fetch(url)
      const html = await response.text()
      this.updateFromHtml(html)
    } catch (error) {
      console.error("Error fetching cards:", error)
    } finally {
      this.toggleLoader(false)
    }
  }

  toggleLoader(show) {
    this.cardsTarget.classList.toggle("opacity-50", show)
  }

  updateFromHtml(html) {
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html

    const newCards = tempDiv.querySelector("[data-store-filter-target='cards']")
    if (newCards) {
      this.cardsTarget.innerHTML = newCards.innerHTML
    }

    const newCount = tempDiv.querySelector("[data-store-filter-target='count']")
    if (newCount && this.hasCountTarget) {
      this.countTarget.innerHTML = newCount.innerHTML
    }

    const newPagination = tempDiv.querySelector("[data-store-filter-target='pagination']")
    if (newPagination && this.hasPaginationTarget) {
      this.paginationTarget.innerHTML = newPagination.innerHTML
    }

    const newHeading = tempDiv.querySelector("[data-store-filter-target='headingTitle']")
    if (newHeading && this.hasHeadingTitleTarget) {
      this.headingTitleTarget.innerHTML = newHeading.innerHTML
    }

    const newCategoryList = tempDiv.querySelector("[data-store-filter-target='categoryList']")
    if (newCategoryList && this.hasCategoryListTarget) {
      this.categoryListTarget.innerHTML = newCategoryList.innerHTML
    }
  }

  // --- Search (AJAX) ---
  handleSearchSubmit(event) {
    event.preventDefault()
    const form = this.searchFormTarget
    const url = new URL(window.location.href)
    const q = (this.hasSearchInputTarget ? this.searchInputTarget.value : new FormData(form).get("q")) || ""
    if (q) {
      url.searchParams.set("q", q)
    } else {
      url.searchParams.delete("q")
    }
    history.pushState({}, "", url)
    this.fetchAndUpdate(url)
  }

  handleSearchInput() {
    // Debounce live typing
    clearTimeout(this._searchTimer)
    const url = new URL(window.location.href)
    const q = this.searchInputTarget.value || ""
    if (q) {
      url.searchParams.set("q", q)
    } else {
      url.searchParams.delete("q")
    }
    this._searchTimer = setTimeout(() => {
      history.replaceState({}, "", url)
      this.fetchAndUpdate(url)
    }, 300)
  }

  async fetchAndUpdate(url) {
    this.toggleLoader(true)
    try {
      const response = await fetch(url)
      const html = await response.text()
      this.updateFromHtml(html)
    } catch (error) {
      console.error("Error fetching search results:", error)
    } finally {
      this.toggleLoader(false)
    }
  }

  // --- Categories (AJAX) ---
  handleCategoryForm() {
    const currentURL = new URL(window.location.href)
    const selected = Array.from(this.categoryFormTarget.querySelectorAll("input[type=checkbox]"))
      .filter(cb => cb.checked)
      .map(cb => cb.value)
    if (selected.length) {
      currentURL.searchParams.set('category', selected.join(','))
    } else {
      currentURL.searchParams.delete('category')
    }
    history.pushState({}, "", currentURL)
    this.fetchAndUpdate(currentURL)
  }

  handleCategoryClick(event) {
    // Prefer elements annotated with data-category
    const el = event.target.closest('[data-category]')
    if (el) {
      event.preventDefault()
      const slug = el.dataset.category || ""
      const url = new URL(window.location.href)
      if (slug) {
        url.searchParams.set("category", slug)
      } else {
        url.searchParams.delete("category")
      }
      history.pushState({}, "", url)
      this.fetchAndUpdate(url)
      return
    }
  }
}
